/**
 * Chart primitives.
 *
 * Colour rules these follow, deliberately:
 * - Categorical series take the validated palette in fixed slot order (never
 *   cycled): --series-1 first, --series-2 second. Both modes were checked for
 *   colour-vision separation and contrast against the chart surface.
 * - Status colours come from the board's own status labels, so a bar is the same
 *   colour as the chip it represents. Status hues cannot be re-picked for CVD
 *   safety (green/amber/red is the meaning), so every status mark ships with a
 *   visible label and a table view - colour never carries meaning alone.
 * - One axis per chart, thin marks, recessive grid, and no number printed on
 *   every point.
 */
import { useCallback, useState } from 'react';
import { createPortal } from 'react-dom';
import * as Icon from '../lib/icons.jsx';

/* ------------------------------------------------------------------ tooltip */

function useTooltip() {
  const [tip, setTip] = useState(null);

  const show = useCallback((event, content) => {
    setTip({ x: event.clientX, y: event.clientY, content });
  }, []);
  const hide = useCallback(() => setTip(null), []);

  const node = tip
    ? createPortal(
      <div
        className="chart-tip"
        style={{
          left: Math.min(tip.x + 12, window.innerWidth - 220),
          top: Math.max(8, tip.y - 40),
        }}
      >
        {tip.content}
      </div>,
      document.body,
    )
    : null;

  return { show, hide, node };
}

/* ---------------------------------------------------------------- container */

/**
 * Wraps a chart with its title and an always-available table view of the same
 * numbers - the accessibility fallback for any mark whose colour is doing work.
 */
export function ChartCard({ title, note, table, children, right }) {
  const [showTable, setShowTable] = useState(false);
  return (
    <section className="card">
      <div className="card-head">
        <div style={{ flex: 1, minWidth: 0 }}>
          <div className="chart-title">{title}</div>
          {note && <div className="chart-note">{note}</div>}
        </div>
        {right}
        {table && (
          <button
            type="button"
            className={`icon-btn sm${showTable ? ' active' : ''}`}
            onClick={() => setShowTable((v) => !v)}
            title={showTable ? 'Show chart' : 'Show the numbers'}
            aria-label={showTable ? 'Show chart' : 'Show the numbers'}
          >
            <Icon.Table size={15} />
          </button>
        )}
      </div>
      <div className="card-body">
        {showTable && table ? <DataTable {...table} /> : children}
      </div>
    </section>
  );
}

export function DataTable({ columns, rows }) {
  return (
    <div style={{ overflowX: 'auto' }}>
      <table className="chart-table">
        <thead>
          <tr>{columns.map((column) => <th key={column}>{column}</th>)}</tr>
        </thead>
        <tbody>
          {rows.map((row, index) => (
            <tr key={index}>
              {row.map((cell, cellIndex) => <td key={cellIndex}>{cell}</td>)}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

/* -------------------------------------------------------------- segmented bar */

/** Part-to-whole across a handful of statuses. Legend carries label + count. */
export function SegmentedBar({ segments, total }) {
  const tooltip = useTooltip();
  const sum = total ?? segments.reduce((acc, segment) => acc + segment.value, 0);
  if (!sum) return <p style={{ fontSize: 13, color: 'var(--muted)' }}>Nothing to show yet.</p>;

  return (
    <>
      <div className="stack-bar" onMouseLeave={tooltip.hide}>
        {segments.filter((segment) => segment.value > 0).map((segment) => (
          <i
            key={segment.id}
            style={{ background: segment.color, width: `${(segment.value / sum) * 100}%` }}
            onMouseMove={(event) => tooltip.show(event, (
              <><b>{segment.label}</b> · {segment.value} ({Math.round((segment.value / sum) * 100)}%)</>
            ))}
            onMouseLeave={tooltip.hide}
          />
        ))}
      </div>
      <div className="legend">
        {segments.map((segment) => (
          <span className="legend-item" key={segment.id}>
            <span className="legend-swatch" style={{ background: segment.color }} />
            {segment.label}
            <span className="legend-value">{segment.value}</span>
          </span>
        ))}
      </div>
      {tooltip.node}
    </>
  );
}

/* ------------------------------------------------------------ horizontal bars */

/**
 * One measure per row, sorted by the caller. `rows` is
 * [{ id, label, sublabel, value, color, meta }]. Bars are thin with a rounded
 * data-end and every row is directly labelled, so the colour is reinforcement.
 */
export function HBars({ rows, max, formatValue = (v) => v, labelWidth = 190 }) {
  const tooltip = useTooltip();
  const ceiling = max ?? Math.max(1, ...rows.map((row) => row.value));

  return (
    <>
      <div>
        {rows.map((row) => (
          // The label width travels as a CSS variable, not an inline
          // grid-template-columns: inline, it silently overrode every phone
          // media query and the workload chart broke at 375pt.
          <div className="workload-row" key={row.id} style={{ '--workload-label': `${labelWidth}px` }}>
            <div className="workload-person">
              {row.avatar}
              <div style={{ minWidth: 0 }}>
                <div className="workload-name">{row.label}</div>
                {row.sublabel && <div className="workload-title">{row.sublabel}</div>}
              </div>
            </div>
            <div
              style={{ height: 20, display: 'flex', alignItems: 'center' }}
              onMouseMove={(event) => tooltip.show(event, <><b>{row.label}</b> · {row.meta || formatValue(row.value)}</>)}
              onMouseLeave={tooltip.hide}
            >
              {row.segments
                ? (
                  <span className="stack-bar" style={{ width: `${(row.value / ceiling) * 100}%`, minWidth: 4, height: 16 }}>
                    {row.segments.filter((segment) => segment.value > 0).map((segment) => (
                      <i
                        key={segment.id}
                        style={{
                          background: segment.color,
                          width: `${(segment.value / Math.max(1, row.value)) * 100}%`,
                        }}
                      />
                    ))}
                  </span>
                )
                : (
                  <span
                    style={{
                      display: 'block',
                      height: 16,
                      width: `${(row.value / ceiling) * 100}%`,
                      minWidth: 4,
                      borderRadius: '2px 4px 4px 2px',
                      background: row.color || 'var(--series-1)',
                    }}
                  />
                )}
            </div>
            <div className="workload-num">{formatValue(row.value)}</div>
          </div>
        ))}
      </div>
      {tooltip.node}
    </>
  );
}

/* ------------------------------------------------------------------- trend */

/**
 * Two-series line chart over time. One y-axis by design: both series count items,
 * so they share a scale and never need a second one.
 */
export function TrendChart({ points, series, height = 190, formatX = (x) => x }) {
  const tooltip = useTooltip();
  const [hover, setHover] = useState(null);

  const width = 640;
  const pad = { top: 14, right: 14, bottom: 26, left: 34 };
  const plotW = width - pad.left - pad.right;
  const plotH = height - pad.top - pad.bottom;

  const maxValue = Math.max(1, ...points.flatMap((point) => series.map((s) => point[s.key])));
  const ticks = niceTicks(maxValue, 4);
  const top = ticks[ticks.length - 1];

  const xOf = (index) => pad.left + (points.length === 1 ? plotW / 2 : (index / (points.length - 1)) * plotW);
  const yOf = (value) => pad.top + plotH - (value / top) * plotH;

  const pathFor = (key) => points
    .map((point, index) => `${index === 0 ? 'M' : 'L'} ${xOf(index).toFixed(1)} ${yOf(point[key]).toFixed(1)}`)
    .join(' ');

  return (
    <>
      <div className="chart-figure">
        <svg
          className="chart-svg"
          viewBox={`0 0 ${width} ${height}`}
          role="img"
          aria-label={`${series.map((s) => s.label).join(' and ')} per week`}
          onMouseLeave={() => { setHover(null); tooltip.hide(); }}
          onMouseMove={(event) => {
            const rect = event.currentTarget.getBoundingClientRect();
            const ratio = ((event.clientX - rect.left) / rect.width) * width;
            const index = Math.round(((ratio - pad.left) / plotW) * (points.length - 1));
            const clamped = Math.max(0, Math.min(points.length - 1, index));
            setHover(clamped);
            tooltip.show(event, (
              <>
                <b>{formatX(points[clamped].week)}</b>
                {series.map((s) => (
                  <span key={s.key} style={{ display: 'block' }}>
                    {s.label}: <b>{points[clamped][s.key]}</b>
                  </span>
                ))}
              </>
            ));
          }}
        >
          {ticks.map((tick) => (
            <g key={tick}>
              <line
                x1={pad.left} x2={width - pad.right} y1={yOf(tick)} y2={yOf(tick)}
                stroke="var(--chart-grid)" strokeWidth="1"
              />
              <text
                x={pad.left - 7} y={yOf(tick) + 3.5} textAnchor="end"
                fontSize="10.5" fill="var(--chart-ink-2)" style={{ fontVariantNumeric: 'tabular-nums' }}
              >
                {tick}
              </text>
            </g>
          ))}

          {points.map((point, index) => (
            index % 2 === 0 && (
              <text
                key={point.week}
                x={xOf(index)} y={height - 8} textAnchor="middle"
                fontSize="10.5" fill="var(--chart-ink-2)"
              >
                {formatX(point.week)}
              </text>
            )
          ))}

          {hover != null && (
            <line
              x1={xOf(hover)} x2={xOf(hover)} y1={pad.top} y2={pad.top + plotH}
              stroke="var(--chart-axis)" strokeWidth="1" strokeDasharray="3 3"
            />
          )}

          {series.map((s) => (
            <path
              key={s.key}
              d={pathFor(s.key)}
              fill="none"
              stroke={s.color}
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          ))}

          {series.map((s) => points.map((point, index) => (
            <circle
              key={`${s.key}-${point.week}`}
              cx={xOf(index)}
              cy={yOf(point[s.key])}
              r={hover === index ? 5 : 3.5}
              fill={s.color}
              stroke="var(--chart-surface)"
              strokeWidth="2"
            />
          )))}
        </svg>
      </div>
      <div className="legend">
        {series.map((s) => (
          <span className="legend-item" key={s.key}>
            <span className="legend-swatch" style={{ background: s.color }} />
            {s.label}
            <span className="legend-value">
              {points.reduce((sum, point) => sum + point[s.key], 0)}
            </span>
          </span>
        ))}
      </div>
      {tooltip.node}
    </>
  );
}

/** Rounded axis ticks from 0 to just above `max`. */
function niceTicks(max, count) {
  const raw = max / count;
  const magnitude = 10 ** Math.floor(Math.log10(Math.max(raw, 1)));
  const step = [1, 2, 2.5, 5, 10].map((m) => m * magnitude).find((s) => s >= raw) || magnitude * 10;
  const ticks = [];
  for (let value = 0; value <= max + step * 0.001; value += step) ticks.push(Math.round(value * 100) / 100);
  if (ticks[ticks.length - 1] < max) ticks.push(ticks[ticks.length - 1] + step);
  return ticks;
}

/** Compact ring for a single percentage. Always paired with the number itself. */
export function ProgressRing({ percent, size = 92, label }) {
  const stroke = 9;
  const radius = (size - stroke) / 2;
  const circumference = 2 * Math.PI * radius;
  const offset = circumference * (1 - Math.max(0, Math.min(100, percent)) / 100);

  return (
    <div style={{ display: 'grid', placeItems: 'center', gap: 6 }}>
      <svg width={size} height={size} role="img" aria-label={`${percent}% ${label || 'complete'}`}>
        <circle
          cx={size / 2} cy={size / 2} r={radius}
          fill="none" stroke="var(--chart-grid)" strokeWidth={stroke}
        />
        <circle
          cx={size / 2} cy={size / 2} r={radius}
          fill="none" stroke="var(--series-3)" strokeWidth={stroke} strokeLinecap="round"
          strokeDasharray={circumference} strokeDashoffset={offset}
          transform={`rotate(-90 ${size / 2} ${size / 2})`}
        />
        <text
          x="50%" y="50%" dy="6" textAnchor="middle"
          fontSize="21" fontWeight="700" fill="var(--chart-ink)"
        >
          {percent}%
        </text>
      </svg>
      {label && <div style={{ fontSize: 12.5, color: 'var(--text-2)' }}>{label}</div>}
    </div>
  );
}
